import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import Navigation from '../components/Navigation';
import TutorialHint from '../components/TutorialHint';
import { formatLocalDate } from '../utils/date';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Heart, Globe, Users, Plus, Check, Trash2, ExternalLink, HandHelping, Edit3, AlertCircle, Sparkles, Send, BellOff, Bell, CheckCircle2 } from 'lucide-react';
import MyGroupsPanel from '../components/MyGroupsPanel';
import MyGroupPrayers from '../components/MyGroupPrayers';

const PrayerWall = () => {
  const { user, API } = useContext(AuthContext);
  const [publicPrayers, setPublicPrayers] = useState([]);
  const [answeredPrayers, setAnsweredPrayers] = useState([]);
  const [myPrayers, setMyPrayers] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // URL-controlled active tab so /prayers?tab=groups lands on the right tab.
  const _validTabs = ['public', 'answered', 'my', 'groups', 'echo'];
  const _initialTab = (() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return _validTabs.includes(t) ? t : 'public';
  })();
  const [activeTab, setActiveTab] = useState(_initialTab);

  // Form state
  const [prayerText, setPrayerText] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [prayerType, setPrayerType] = useState('urgent');
  const [category, setCategory] = useState('personal');
  const [postedForName, setPostedForName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPrayer, setEditingPrayer] = useState(null);
  const [editText, setEditText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // "Post an Update" dialog — timestamped progress note
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [updatingPrayer, setUpdatingPrayer] = useState(null);
  const [updateText, setUpdateText] = useState('');
  const [postingUpdate, setPostingUpdate] = useState(false);

  // "Final update & close" dialog — posts one last update then stops all pings
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closingPrayer, setClosingPrayer] = useState(null);
  const [finalText, setFinalText] = useState('');
  const [closingUpdates, setClosingUpdates] = useState(false);

  // Snooze / resume / reopen are quick single-click actions; track in-flight id.
  const [notifBusyId, setNotifBusyId] = useState(null);

  // Track in-flight "pray" taps so we can block double-fire on slow networks.
  const [prayingId, setPrayingId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [publicRes, answeredRes, myPrayersRes, groupsRes] = await Promise.all([
        axios.get(`${API}/prayers/public`),
        axios.get(`${API}/prayers/answered`),
        axios.get(`${API}/prayers/my`),
        axios.get(`${API}/groups/my`)
      ]);

      setPublicPrayers(publicRes.data);
      setAnsweredPrayers(answeredRes.data);
      setMyPrayers(myPrayersRes.data);
      setMyGroups(groupsRes.data);
    } catch (error) {
      toast.error('Failed to load prayers');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Deep-link from a follow-up notification: /prayers?update=<prayer_id>
  // If the current user is the author, auto-open the "Post an Update" dialog.
  useEffect(() => {
    if (!myPrayers.length) return;
    const params = new URLSearchParams(window.location.search);
    const targetId = params.get('update');
    if (!targetId) return;
    const target = myPrayers.find((p) => p.id === targetId);
    if (target) {
      setUpdatingPrayer(target);
      setUpdateText('');
      setUpdateDialogOpen(true);
      // Clean up the URL so it doesn't retrigger on refresh
      const clean = new URL(window.location.href);
      clean.searchParams.delete('update');
      window.history.replaceState({}, '', clean.toString());
    }
  }, [myPrayers]);

  const handleSubmitPrayer = async () => {
    if (!prayerText.trim()) {
      toast.error('Please enter a prayer request');
      return;
    }

    // Confidential prayers force a special visibility and skip the group selector.
    const isConfidential = prayerType === 'confidential';
    const effectiveVisibility = isConfidential ? 'confidential' : visibility;

    if (effectiveVisibility === 'group' && !selectedGroup) {
      toast.error('Please select a group');
      return;
    }

    setSubmitting(true);
    try {
      const isPrayerAdmin = user?.role === 'admin' || !!user?.is_prayer_moderator;
      await axios.post(`${API}/prayers`, {
        prayer_text: prayerText,
        visibility: effectiveVisibility,
        group_id: effectiveVisibility === 'group' ? selectedGroup : null,
        prayer_type: prayerType,
        category: category,
        posted_for_name: (isPrayerAdmin && postedForName.trim()) ? postedForName.trim() : undefined,
      });

      if (isConfidential) {
        toast.success('Confidential request submitted — only prayer moderators will see it.');
      } else if (effectiveVisibility === 'group') {
        toast.success('Prayer shared with your group.');
      } else if (isPrayerAdmin && postedForName.trim()) {
        toast.success(`Prayer posted on behalf of ${postedForName.trim()}.`);
      } else {
        toast.success('Prayer submitted — pending admin approval before it appears on the wall');
      }
      setPrayerText('');
      setCategory('personal');
      setPrayerType('urgent');
      setPostedForName('');
      setCreateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share prayer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkAnswered = async (prayerId, answered) => {
    try {
      await axios.put(`${API}/prayers/${prayerId}`, { answered });
      toast.success(answered ? 'Marked as answered! Praise God!' : 'Unmarked as answered');
      fetchData();
    } catch (error) {
      toast.error('Failed to update prayer');
    }
  };

  const handleDeletePrayer = async (prayerId) => {
    if (!window.confirm('Are you sure you want to delete this prayer?')) return;

    try {
      await axios.delete(`${API}/prayers/${prayerId}`);
      toast.success('Prayer deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete prayer');
    }
  };

  const handlePray = async (prayer) => {
    if (prayingId === prayer.id) return; // already in-flight
    setPrayingId(prayer.id);
    try {
      const res = await axios.post(`${API}/prayers/${prayer.id}/pray`);
      const { prayer_count, unique_prayer_count, has_prayed } = res.data || {};
      // Update both lists in-place so the counter increments immediately.
      const apply = (list) =>
        list.map((p) =>
          p.id === prayer.id
            ? {
                ...p,
                prayer_count,
                prayer_user_ids: has_prayed
                  ? Array.from(new Set([...(p.prayer_user_ids || []), user.id]))
                  : p.prayer_user_ids,
              }
            : p,
        );
      setPublicPrayers((prev) => apply(prev));
      setAnsweredPrayers((prev) => apply(prev));
      toast.success(
        has_prayed && unique_prayer_count === 1
          ? 'Thank you for praying. The poster will be notified.'
          : 'Your prayer has been recorded',
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not record prayer');
    } finally {
      setPrayingId(null);
    }
  };

  const openEditDialog = (prayer) => {
    setEditingPrayer(prayer);
    // Show pending text if it exists (so user can iterate on their pending edit).
    setEditText(prayer.pending_text || prayer.prayer_text);
    setEditDialogOpen(true);
  };

  const handleSubmitEdit = async () => {
    if (!editingPrayer) return;
    const trimmed = editText.trim();
    if (!trimmed) {
      toast.error('Prayer text cannot be empty');
      return;
    }
    if (trimmed === (editingPrayer.prayer_text || '').trim() && !editingPrayer.edit_pending) {
      toast.info('No changes to save');
      setEditDialogOpen(false);
      return;
    }
    setSavingEdit(true);
    try {
      await axios.put(`${API}/prayers/${editingPrayer.id}`, { prayer_text: trimmed });
      if (editingPrayer.approved) {
        toast.success('Edit submitted — your update is pending admin approval');
      } else {
        toast.success('Prayer updated');
      }
      setEditDialogOpen(false);
      setEditingPrayer(null);
      setEditText('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update prayer');
    } finally {
      setSavingEdit(false);
    }
  };

  // "Post an Update" flow — appends a timestamped note to the prayer.
  const openUpdateDialog = (prayer) => {
    setUpdatingPrayer(prayer);
    setUpdateText('');
    setUpdateDialogOpen(true);
  };

  const handlePostUpdate = async () => {
    if (!updatingPrayer) return;
    const trimmed = updateText.trim();
    if (!trimmed) {
      toast.error('Update text cannot be empty');
      return;
    }
    setPostingUpdate(true);
    try {
      await axios.post(`${API}/prayers/${updatingPrayer.id}/updates`, { text: trimmed });
      toast.success('Update posted — thanks for the news!');
      setUpdateDialogOpen(false);
      setUpdatingPrayer(null);
      setUpdateText('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post update');
    } finally {
      setPostingUpdate(false);
    }
  };

  // Snooze / resume update pings on a prayer.
  const handleSnooze = async (prayer, days) => {
    setNotifBusyId(prayer.id);
    try {
      await axios.post(`${API}/prayers/${prayer.id}/notifications/snooze`, { days });
      toast.success(`Notifications paused for ${days} day${days === 1 ? '' : 's'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to snooze notifications');
    } finally {
      setNotifBusyId(null);
    }
  };

  const handleResumeNotifications = async (prayer) => {
    setNotifBusyId(prayer.id);
    try {
      await axios.post(`${API}/prayers/${prayer.id}/notifications/resume`);
      toast.success('Notifications resumed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resume notifications');
    } finally {
      setNotifBusyId(null);
    }
  };

  const handleReopenUpdates = async (prayer) => {
    setNotifBusyId(prayer.id);
    try {
      await axios.post(`${API}/prayers/${prayer.id}/updates/reopen`);
      toast.success('Updates reopened — pings will resume');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen updates');
    } finally {
      setNotifBusyId(null);
    }
  };

  // "Final update & close" — one last optional update, then permanently stop
  // notifications. If finalText is empty we just close silently.
  const openCloseDialog = (prayer) => {
    setClosingPrayer(prayer);
    setFinalText('');
    setCloseDialogOpen(true);
  };

  const handleCloseUpdates = async () => {
    if (!closingPrayer) return;
    setClosingUpdates(true);
    try {
      await axios.post(`${API}/prayers/${closingPrayer.id}/updates/close`, {
        final_text: finalText.trim() || null,
      });
      toast.success('Updates closed — no more notifications will be sent');
      setCloseDialogOpen(false);
      setClosingPrayer(null);
      setFinalText('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close updates');
    } finally {
      setClosingUpdates(false);
    }
  };

  const formatDate = (dateStr) => formatLocalDate(dateStr, 'relative');

  const getPrayerTypeColor = (type) => {
    const colors = {
      urgent: 'bg-red-100 text-red-800 border-red-200',
      ongoing: 'bg-blue-100 text-blue-800 border-blue-200',
      short_term: 'bg-purple-100 text-purple-800 border-purple-200',
      praise: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      confidential: 'bg-slate-200 text-slate-800 border-slate-300',
      // legacy fallbacks
      petition: 'bg-blue-100 text-blue-800 border-blue-200',
      intercession: 'bg-purple-100 text-purple-800 border-purple-200',
      thanksgiving: 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[type] || colors.ongoing;
  };

  const getPrayerTypeIcon = (type) => {
    const labels = {
      urgent: 'Urgent',
      ongoing: 'Ongoing',
      short_term: 'Short Term',
      praise: 'Praise',
      confidential: 'Confidential',
    };
    return labels[type] || (type.charAt(0).toUpperCase() + type.slice(1));
  };

  // Decide which prayers to show on the Public tab based on the active category pill.
  // "answered" pulls from the separate answered feed; everything else filters the public list.
  const visiblePublicPrayers =
    selectedCategory === 'answered'
      ? answeredPrayers
      : publicPrayers.filter(p => selectedCategory === 'all' || p.category === selectedCategory);

  // Snapshot "now" at mount so the cutoff is stable across re-renders (keeps useMemo pure).
  const [renderedAt] = useState(() => Date.now());

  // 🎉 Praise Report: how many public prayers have been marked answered in the last 7 days?
  const praiseReportCount = useMemo(() => {
    const cutoff = renderedAt - 7 * 24 * 60 * 60 * 1000;
    return answeredPrayers.filter((p) => {
      const ts = p.answered_at ? new Date(p.answered_at).getTime() : 0;
      return ts >= cutoff;
    }).length;
  }, [answeredPrayers, renderedAt]);

  const renderPrayerCard = (prayer, showActions = false) => {
    const isMine = user && prayer.user_id === user.id;
    const isAdminOrMod = user && (user.role === 'admin' || user.is_prayer_moderator);
    const canManageNotifs = isMine || isAdminOrMod;
    const userHasPrayed = !!(user && (prayer.prayer_user_ids || []).includes(user.id));
    const prayerCount = prayer.prayer_count || 0;
    const uniqueCount = (prayer.prayer_user_ids || []).length;
    const canPray = !isMine && prayer.approved && !prayer.answered;
    const isSnoozed = !!(prayer.notifications_snoozed_until && new Date(prayer.notifications_snoozed_until) > new Date());
    const snoozedUntilLabel = isSnoozed ? formatLocalDate(prayer.notifications_snoozed_until, { month: 'short', day: 'numeric' }) : '';

    return (
      <Card
        key={prayer.id}
        className={`${prayer.answered ? 'border-green-300 bg-green-50/30' : ''} ${!prayer.approved ? 'border-amber-300 bg-amber-50/30' : ''}`}
        data-testid={`prayer-card-${prayer.id}`}
      >
        <CardContent className="pt-6">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-[#2B6B7F] text-white flex items-center justify-center font-semibold">
                {prayer.user_name.charAt(0)}
              </div>
              <div>
                <p className="font-semibold text-slate-800">{prayer.user_name}</p>
                <p className="text-xs text-slate-500">{formatDate(prayer.created_at)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Badge className={getPrayerTypeColor(prayer.prayer_type)} variant="outline">
                {getPrayerTypeIcon(prayer.prayer_type)}
              </Badge>
              {prayer.visibility === 'public' && prayer.category && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  {prayer.category === 'global' && '🌍 Global'}
                  {prayer.category === 'local' && '🏘️ Local'}
                  {prayer.category === 'personal' && '👤 Personal'}
                  {prayer.category === 'lost' && '✝️ Lost'}
                  {prayer.category === 'answered' && '✅ Answered'}
                </Badge>
              )}
              {prayer.visibility === 'group' && (
                <Badge variant="outline" className="bg-slate-100">
                  <Users className="w-3 h-3 mr-1" />
                  Group
                </Badge>
              )}
              {prayer.answered && (
                <Badge className="bg-green-600 text-white">
                  <Check className="w-3 h-3 mr-1" />
                  Answered
                </Badge>
              )}
              {!prayer.approved && (
                <Badge className="bg-amber-500 text-white" data-testid="badge-pending-approval">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Pending approval
                </Badge>
              )}
              {prayer.approved && prayer.edit_pending && isMine && (
                <Badge className="bg-blue-500 text-white" data-testid="badge-edit-pending">
                  <Edit3 className="w-3 h-3 mr-1" />
                  Edit pending
                </Badge>
              )}
              {prayer.updates_closed && (
                <Badge className="bg-slate-600 text-white" data-testid={`badge-closed-${prayer.id}`}>
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Closed for updates
                </Badge>
              )}
              {!prayer.updates_closed && isSnoozed && (
                <Badge className="bg-amber-100 text-amber-800 border-amber-300" variant="outline" data-testid={`badge-snoozed-${prayer.id}`}>
                  <BellOff className="w-3 h-3 mr-1" />
                  Paused until {snoozedUntilLabel}
                </Badge>
              )}
            </div>
          </div>

          <p className="text-slate-700 leading-relaxed mb-3 whitespace-pre-wrap">
            {prayer.prayer_text}
          </p>

          {/* Author-only: show pending edit text inline so they remember what's queued */}
          {isMine && prayer.edit_pending && prayer.pending_text && (
            <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm" data-testid="pending-edit-preview">
              <p className="text-xs font-semibold text-blue-700 mb-1 uppercase tracking-wide">Your pending edit (awaiting approval):</p>
              <p className="text-slate-700 whitespace-pre-wrap">{prayer.pending_text}</p>
            </div>
          )}

          {/* Prayer count display — AUTHOR ONLY. Non-authors don't see the count so nobody
              feels discouraged if their request has fewer prayers than someone else's. */}
          {isMine && prayer.approved && prayerCount > 0 && (
            <div className="flex items-center gap-2 mb-3 text-sm text-[#2B6B7F]" data-testid={`prayer-count-${prayer.id}`}>
              <HandHelping className="w-4 h-4" />
              <span>
                <strong>{prayerCount}</strong> prayer{prayerCount === 1 ? '' : 's'} from{' '}
                <strong>{uniqueCount}</strong> {uniqueCount === 1 ? 'person' : 'people'}
              </span>
            </div>
          )}
          {/* Non-authors just see a subtle "You prayed" chip if applicable, no counts. */}
          {!isMine && userHasPrayed && (
            <div className="mb-3">
              <span className="text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full" data-testid={`you-prayed-chip-${prayer.id}`}>
                You prayed for this
              </span>
            </div>
          )}

          {/* Timeline of author-posted updates */}
          {(prayer.updates || []).length > 0 && (
            <div className="mb-3 space-y-2" data-testid={`prayer-updates-${prayer.id}`}>
              {(prayer.updates || []).map((u, idx) => (
                <div key={idx} className="pl-3 border-l-2 border-[#2B6B7F]/40 text-sm">
                  <p className="text-xs text-slate-500 mb-0.5">
                    Update · {formatDate(u.created_at)}
                  </p>
                  <p className="text-slate-700 whitespace-pre-wrap">{u.text}</p>
                </div>
              ))}
            </div>
          )}

          {/* Pray button for others' approved prayers */}
          {canPray && (
            <div className="pt-2">
              <Button
                size="sm"
                onClick={() => handlePray(prayer)}
                disabled={prayingId === prayer.id}
                className="bg-[#2B6B7F] hover:bg-[#1E4D5F] text-white"
                data-testid={`pray-button-${prayer.id}`}
              >
                <HandHelping className="w-4 h-4 mr-2" />
                {prayingId === prayer.id ? 'Recording…' : userHasPrayed ? 'Pray Again' : 'I Prayed For This'}
              </Button>
            </div>
          )}

          {/* Author actions */}
          {showActions && (
            <div className="flex gap-2 pt-3 border-t border-slate-200 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                onClick={() => openUpdateDialog(prayer)}
                className="border-[#2B6B7F] text-[#2B6B7F] hover:bg-[#2B6B7F]/10"
                data-testid={`post-update-button-${prayer.id}`}
                disabled={prayer.updates_closed}
                title={prayer.updates_closed ? 'Updates are closed on this prayer' : ''}
              >
                <Send className="w-4 h-4 mr-1" />
                Post an Update
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => openEditDialog(prayer)}
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                data-testid={`edit-button-${prayer.id}`}
              >
                <Edit3 className="w-4 h-4 mr-1" />
                Edit
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleMarkAnswered(prayer.id, !prayer.answered)}
                className="border-green-300 text-green-700 hover:bg-green-50"
                data-testid={`mark-answered-${prayer.id}`}
              >
                <Sparkles className="w-4 h-4 mr-1" />
                {prayer.answered ? 'Unmark Answered' : 'Mark as Answered'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDeletePrayer(prayer.id)}
                className="border-red-300 text-red-600 hover:bg-red-50"
                data-testid={`delete-button-${prayer.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Notification controls — author / admin / prayer moderator.
              Only shown for approved, unanswered prayers (otherwise there are
              no automated pings to manage). */}
          {canManageNotifs && prayer.approved && !prayer.answered && (
            <div className="pt-3 mt-3 border-t border-slate-200 flex gap-2 flex-wrap items-center" data-testid={`notif-controls-${prayer.id}`}>
              <span className="text-xs text-slate-500 mr-1">Pings:</span>
              {prayer.updates_closed ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleReopenUpdates(prayer)}
                  disabled={notifBusyId === prayer.id}
                  className="border-slate-300 text-slate-700 hover:bg-slate-100"
                  data-testid={`reopen-updates-${prayer.id}`}
                >
                  <Bell className="w-4 h-4 mr-1" />
                  Reopen updates
                </Button>
              ) : (
                <>
                  {isSnoozed ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleResumeNotifications(prayer)}
                      disabled={notifBusyId === prayer.id}
                      className="border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid={`resume-notifs-${prayer.id}`}
                    >
                      <Bell className="w-4 h-4 mr-1" />
                      Resume now
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSnooze(prayer, 1)}
                        disabled={notifBusyId === prayer.id}
                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        data-testid={`snooze-1d-${prayer.id}`}
                      >
                        <BellOff className="w-4 h-4 mr-1" />
                        Snooze 1d
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSnooze(prayer, 3)}
                        disabled={notifBusyId === prayer.id}
                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        data-testid={`snooze-3d-${prayer.id}`}
                      >
                        <BellOff className="w-4 h-4 mr-1" />
                        Snooze 3d
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openCloseDialog(prayer)}
                    disabled={notifBusyId === prayer.id}
                    className="border-slate-400 text-slate-700 hover:bg-slate-100"
                    data-testid={`close-updates-${prayer.id}`}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Final update & close
                  </Button>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
        <Navigation />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-lg text-slate-600">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50" data-testid="prayer-wall-page">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="mb-8 animate-fade-in">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-4xl font-heading font-bold text-[#2B6B7F]">
                  Prayer Wall
                </h1>
                <TutorialHint videoId="Q5iAgZeOywM" />
              </div>
              <p className="text-slate-600">Share prayer requests and celebrate answered prayers</p>
            </div>
            <div className="flex items-center gap-2">
              {/* Share Prayer Button */}
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#2B6B7F] hover:bg-[#1E4D5F]">
                    <Plus className="w-5 h-5 mr-2" />
                    Share Prayer
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Share a Prayer Request</DialogTitle>
                  <DialogDescription>
                    Let others join you in prayer
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Prayer Type</label>
                    <Select value={prayerType} onValueChange={setPrayerType}>
                      <SelectTrigger data-testid="prayer-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="urgent">Urgent — Immediate need, we'll check in every 24h</SelectItem>
                        <SelectItem value="ongoing">Ongoing — Long-haul, we'll check in every 6 weeks</SelectItem>
                        <SelectItem value="short_term">Short Term — Single event, we'll check in after 5 days</SelectItem>
                        <SelectItem value="praise">Praise — Testimony / worship</SelectItem>
                        <SelectItem value="confidential">Confidential — Prayer Moderators only, never public</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {visibility === 'public' && (
                    <div>
                      <label className="text-sm font-medium mb-2 block">Prayer Category</label>
                      <Select value={category} onValueChange={setCategory}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="global">Global Prayers - World events, missions</SelectItem>
                          <SelectItem value="local">Local Prayers - Community, church needs</SelectItem>
                          <SelectItem value="personal">Personal Prayers - Individual needs</SelectItem>
                          <SelectItem value="lost">Prayers for the Lost - Evangelism, salvation</SelectItem>
                          <SelectItem value="answered">Answered Prayers - Testimonies</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {prayerType !== 'confidential' && (
                    <div>
                      <label className="text-sm font-medium mb-2 block">Share With</label>
                      <Select value={visibility} onValueChange={setVisibility}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="public">
                            <div className="flex items-center gap-2">
                              <Globe className="w-4 h-4" />
                              Everyone at Church
                            </div>
                          </SelectItem>
                          <SelectItem value="group">
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4" />
                              My Group Only
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {prayerType === 'confidential' && (
                    <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
                      <strong>Confidential:</strong> Only prayer moderators will see this request. It will never appear on the public wall or any group feed.
                    </div>
                  )}

                  {visibility === 'group' && (
                    <div>
                      <label className="text-sm font-medium mb-2 block">Select Group</label>
                      <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a group" />
                        </SelectTrigger>
                        <SelectContent>
                          {myGroups.map((group) => (
                            <SelectItem key={group.id} value={group.id}>
                              {group.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium mb-2 block">Prayer Request</label>
                    <Textarea
                      placeholder="Share what's on your heart..."
                      value={prayerText}
                      onChange={(e) => setPrayerText(e.target.value)}
                      rows={6}
                      className="resize-none"
                    />
                  </div>

                  {/* Post-on-behalf-of — prayer admins & moderators only */}
                  {(user?.role === 'admin' || user?.is_prayer_moderator) && (
                    <div className="rounded-lg border border-teal-200 bg-teal-50/40 p-3 space-y-1">
                      <label className="text-sm font-medium text-teal-800 flex items-center gap-1">
                        Posting on behalf of
                        <span className="text-xs font-normal text-slate-500">(optional)</span>
                      </label>
                      <Input
                        placeholder="e.g. Sarah Doe"
                        value={postedForName}
                        onChange={(e) => setPostedForName(e.target.value)}
                        data-testid="posted-for-name-input"
                      />
                      <p className="text-xs text-slate-500">
                        Leave blank to post as yourself. When filled, the prayer shows their name
                        on the wall and is auto-approved.
                      </p>
                    </div>
                  )}

                  <Button
                    onClick={handleSubmitPrayer}
                    disabled={submitting}
                    className="w-full bg-[#2B6B7F] hover:bg-[#1E4D5F]"
                  >
                    {submitting ? 'Sharing...' : 'Share Prayer'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 h-auto gap-1">
            <TabsTrigger value="public" className="flex flex-col items-center gap-1 py-2 px-1 h-auto text-center" data-testid="tab-public">
              <Globe className="w-4 h-4 flex-shrink-0" />
              <span className="text-xs leading-tight">Public<br className="sm:hidden" /><span className="hidden sm:inline"> </span>Prayers</span>
            </TabsTrigger>
            <TabsTrigger value="answered" className="flex flex-col items-center gap-1 py-2 px-1 h-auto text-center" data-testid="tab-answered">
              <Sparkles className="w-4 h-4 flex-shrink-0 text-amber-500" />
              <span className="text-xs leading-tight">Answered</span>
            </TabsTrigger>
            <TabsTrigger value="my" className="flex flex-col items-center gap-1 py-2 px-1 h-auto text-center" data-testid="tab-my">
              <Heart className="w-4 h-4 flex-shrink-0" />
              <span className="text-xs leading-tight">My<br className="sm:hidden" /><span className="hidden sm:inline"> </span>Prayers</span>
            </TabsTrigger>
            <TabsTrigger value="groups" className="flex flex-col items-center gap-1 py-2 px-1 h-auto text-center" data-testid="tab-groups">
              <Users className="w-4 h-4 flex-shrink-0 text-[#2B6B7F]" />
              <span className="text-xs leading-tight">Groups</span>
            </TabsTrigger>
            <TabsTrigger value="echo" className="flex flex-col items-center gap-1 py-2 px-1 h-auto text-center" data-testid="tab-echo">
              <ExternalLink className="w-4 h-4 flex-shrink-0 text-purple-500" />
              <span className="text-xs leading-tight">Echo<br className="sm:hidden" /><span className="hidden sm:inline"> </span>Prayer</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="public" className="space-y-4">
            {/* 🎉 Praise Report ribbon — animated banner when there are fresh answered prayers this week */}
            {praiseReportCount > 0 && selectedCategory !== 'answered' && (
              <button
                type="button"
                onClick={() => setSelectedCategory('answered')}
                className="praise-ribbon w-full rounded-xl px-4 py-3 sm:px-5 sm:py-4 flex items-center justify-between gap-3 transition-all"
                data-testid="praise-report-ribbon"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-2xl sm:text-3xl flex-shrink-0" aria-hidden>🎉</span>
                  <div className="text-left min-w-0">
                    <p className="font-bold text-base sm:text-lg leading-tight">
                      Praise Report
                    </p>
                    <p className="text-xs sm:text-sm text-white/90 leading-tight">
                      {praiseReportCount === 1
                        ? '1 prayer answered this week — give thanks!'
                        : `${praiseReportCount} prayers answered this week — give thanks!`}
                    </p>
                  </div>
                </div>
                <span className="flex items-center gap-1 text-xs sm:text-sm font-semibold whitespace-nowrap bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-full transition-colors">
                  See testimonies
                  <Sparkles className="w-3.5 h-3.5" />
                </span>
              </button>
            )}

            {/* Category Filter */}
            <Card className="bg-gradient-to-br from-blue-50 to-teal-50 border-blue-200">
              <CardContent className="pt-6">
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant={selectedCategory === 'all' ? 'default' : 'outline'}
                    onClick={() => setSelectedCategory('all')}
                    className={selectedCategory === 'all' ? 'bg-[#2B6B7F]' : ''}
                  >
                    All Prayers
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedCategory === 'global' ? 'default' : 'outline'}
                    onClick={() => setSelectedCategory('global')}
                    className={selectedCategory === 'global' ? 'bg-[#2B6B7F]' : ''}
                  >
                    🌍 Global
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedCategory === 'local' ? 'default' : 'outline'}
                    onClick={() => setSelectedCategory('local')}
                    className={selectedCategory === 'local' ? 'bg-[#5FA8C1]' : ''}
                  >
                    🏘️ Local
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedCategory === 'personal' ? 'default' : 'outline'}
                    onClick={() => setSelectedCategory('personal')}
                    className={selectedCategory === 'personal' ? 'bg-[#7BA888]' : ''}
                  >
                    👤 Personal
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedCategory === 'lost' ? 'default' : 'outline'}
                    onClick={() => setSelectedCategory('lost')}
                    className={selectedCategory === 'lost' ? 'bg-[#D4A574]' : ''}
                  >
                    ✝️ For the Lost
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedCategory === 'answered' ? 'default' : 'outline'}
                    onClick={() => setSelectedCategory('answered')}
                    className={selectedCategory === 'answered' ? 'bg-green-600' : ''}
                  >
                    ✅ Answered
                  </Button>
                </div>
              </CardContent>
            </Card>

            {visiblePublicPrayers.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center py-12">
                  {selectedCategory === 'answered' ? (
                    <>
                      <Sparkles className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-slate-700 mb-2">
                        No Answered Prayers Yet
                      </h3>
                      <p className="text-slate-600">
                        Once a public prayer is marked answered it will appear here as a testimony.
                      </p>
                    </>
                  ) : (
                    <>
                      <Globe className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-slate-700 mb-2">
                        No Public Prayers Yet
                      </h3>
                      <p className="text-slate-600">
                        Be the first to share a prayer request with the church community
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>
            ) : (
              visiblePublicPrayers.map(prayer => renderPrayerCard(prayer, false))
            )}
          </TabsContent>

          {/* Answered Prayers Tab — public + answered only. Group/confidential
              answered prayers are intentionally excluded. */}
          <TabsContent value="answered" className="space-y-4" data-testid="answered-tab-content">
            <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-7 h-7 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-700">
                      <strong className="text-amber-800">Praise Reports.</strong>{' '}
                      Public prayers the community has celebrated as answered. Give thanks and be encouraged!
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            {answeredPrayers.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center py-12">
                  <Sparkles className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">
                    No Answered Prayers Yet
                  </h3>
                  <p className="text-slate-600">
                    Once a public prayer is marked answered it will show up here.
                  </p>
                </CardContent>
              </Card>
            ) : (
              answeredPrayers.map(prayer => renderPrayerCard(prayer, false))
            )}
          </TabsContent>

          <TabsContent value="my" className="space-y-4">
            {myPrayers.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center py-12">
                  <Heart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">
                    No Prayers Yet
                  </h3>
                  <p className="text-slate-600 mb-4">
                    Start sharing your prayer requests and see how God answers
                  </p>
                  <Button onClick={() => setCreateDialogOpen(true)} className="bg-[#2B6B7F]">
                    <Plus className="w-4 h-4 mr-2" />
                    Share Your First Prayer
                  </Button>
                </CardContent>
              </Card>
            ) : (
              myPrayers.map(prayer => renderPrayerCard(prayer, true))
            )}
          </TabsContent>

          {/* Groups Tab */}
          <TabsContent value="groups" className="space-y-4" data-testid="groups-tab-content">
            <Card className="bg-gradient-to-br from-blue-50 to-teal-50 border-blue-200">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-start gap-3">
                  <Users className="w-7 h-7 text-[#2B6B7F] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-700">
                      <strong className="text-[#2B6B7F]">Private prayer groups.</strong>{' '}
                      Create a group and share its 8-character invite code with people you trust.
                      When you submit a prayer with visibility set to "My Group Only" it stays inside
                      the group you choose.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <MyGroupsPanel
              title="My Prayer Groups"
              description="Invite-only groups for sharing private prayer requests"
            />
            <MyGroupPrayers />
          </TabsContent>

          {/* Echo Prayer Tab */}
          <TabsContent value="echo" className="space-y-6">
            {/* Echo Prayer App Section */}
            <Card className="border-t-4 border-t-purple-500">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                      <ExternalLink className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <CardTitle className="text-xl text-[#2B6B7F]">Echo Prayer App</CardTitle>
                      <CardDescription>
                        Connect with the global prayer community
                      </CardDescription>
                    </div>
                  </div>
                  <a
                    href="https://app.echoprayer.com/user/feeds/4265"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
                  >
                    <span className="hidden sm:inline">Open in New Tab</span>
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {/* Desktop iframe embed */}
                <div className="w-full bg-white">
                  <iframe
                    src="https://app.echoprayer.com/user/feeds/4265"
                    title="Echo Prayer App"
                    className="w-full border-0"
                    style={{ 
                      height: 'calc(100vh - 350px)',
                      minHeight: '600px'
                    }}
                    allow="clipboard-write; microphone"
                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Prayer Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Edit your prayer request</DialogTitle>
              <DialogDescription>
                {editingPrayer?.approved
                  ? 'Your edit will be queued for admin approval. The original text stays visible on the Prayer Wall until your edit is approved.'
                  : 'Update the prayer text below. Since this prayer is not yet approved, your changes will be visible to the admin immediately.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <Textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={6}
                className="resize-none"
                data-testid="edit-prayer-textarea"
              />
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setEditDialogOpen(false)}
                  data-testid="edit-cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitEdit}
                  disabled={savingEdit}
                  className="bg-[#2B6B7F] hover:bg-[#1E4D5F]"
                  data-testid="edit-save-button"
                >
                  {savingEdit ? 'Saving…' : editingPrayer?.approved ? 'Submit for approval' : 'Save changes'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Post an Update Dialog — appends a timestamped progress note. */}
        <Dialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Post an update</DialogTitle>
              <DialogDescription>
                Share how things are going. Your update will be added to this prayer&apos;s timeline so people
                who&apos;ve prayed can celebrate or continue interceding with you.
              </DialogDescription>
            </DialogHeader>
            {updatingPrayer && (
              <div className="p-3 rounded-md bg-slate-50 border border-slate-200 mb-2">
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Original request</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{updatingPrayer.prayer_text}</p>
              </div>
            )}
            <Textarea
              value={updateText}
              onChange={(e) => setUpdateText(e.target.value)}
              rows={5}
              placeholder="e.g. Doctor said the tests came back clear — thank you for praying!"
              data-testid="post-update-textarea"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setUpdateDialogOpen(false)}
                disabled={postingUpdate}
                data-testid="post-update-cancel-button"
              >
                Cancel
              </Button>
              <Button
                onClick={handlePostUpdate}
                disabled={postingUpdate}
                className="bg-[#2B6B7F] hover:bg-[#1E4D5F]"
                data-testid="post-update-save-button"
              >
                {postingUpdate ? 'Posting…' : 'Post Update'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Final Update & Close Dialog — posts one last update (optional) then
            permanently stops notifications on this prayer. */}
        <Dialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
          <DialogContent className="max-w-xl" data-testid="close-updates-dialog">
            <DialogHeader>
              <DialogTitle>Post a final update &amp; close</DialogTitle>
              <DialogDescription>
                Add one last note (optional), then we&apos;ll stop sending any more
                notifications about this prayer. The prayer stays visible on the
                wall — people can still comment and react quietly.
              </DialogDescription>
            </DialogHeader>
            {closingPrayer && (
              <div className="p-3 rounded-md bg-slate-50 border border-slate-200 mb-2">
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Original request</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{closingPrayer.prayer_text}</p>
              </div>
            )}
            <Textarea
              value={finalText}
              onChange={(e) => setFinalText(e.target.value)}
              rows={4}
              placeholder="e.g. Still ongoing but we're resting in God's timing — no more updates needed for now. Thank you all."
              data-testid="final-update-textarea"
            />
            <p className="text-xs text-slate-500">
              Leave blank to close silently with no final notification.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setCloseDialogOpen(false)}
                disabled={closingUpdates}
                data-testid="close-updates-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCloseUpdates}
                disabled={closingUpdates}
                className="bg-slate-700 hover:bg-slate-800"
                data-testid="close-updates-confirm"
              >
                {closingUpdates ? 'Closing…' : 'Close updates'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default PrayerWall;
